import { useEffect, useState } from "react";
import {
  Alert,
  Backdrop,
  Button,
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  TextField
} from "@mui/material";
import Grid from "@mui/material/Unstable_Grid2";
import { useNavigate } from "react-router-dom";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { PageWrapper, PageWrapperProps } from "../../common-ui/PageWrapper";
import { ProgramRole, User, TraineeType } from "../../API";
import {
  selectCurrentUser,
  selectUserCognitoGroups
} from "../Users/usersSlice";
import { useAppSelector } from "../../app/hooks";
import {
  InvitationFormErrors,
  InvitationNetworkErrors,
  useCreateUserInvitation,
  useSendUserInvitation
} from "./userInvitationUtils";
import {
  useCreateUser,
  CognitoUserGroups,
  useDeleteUser,
  useUsersByEmail,
  useUsersByNPI
} from "../Users/userUtils";
import {
  InstitutionNotifications,
  alphabetizeInstitutions
} from "../Institutions/institutionUtils";
import {
  ProgramNetworkErrors,
  ProgramNotifications,
  alphabetizePrograms,
  useCreateProgramMembership
} from "../Programs/programUtils";
import {
  PGYSelectFields,
  ProgramRoleSelectFields,
  TraineeTypeSelectFields,
  Regex,
  UserFormErrors,
  UserNetworkErrors
} from "../../app/constants";
import { getProgramsCurrentUserCanAdminister } from "../../app/utils";
import { AddUserToProgram } from "../Users/AddUserToProgram";
import { useDispatch } from "react-redux";
import { setModalContent, setShowModal } from "../../common-ui/modalSlice";
import { CreateUserInput } from "../Users/usersAPI";
import { create } from "lodash";

type NewUserInvitationFormInputs = {
  firstName: string;
  lastName: string;
  email: string;
  institutionId: string;
  programId: string;
  simplLearnId: string;
  role: NewProgramRole;
  npi: number | null;
  pgy: number | null;
  traineeType: TraineeType | null;
  isClinicianTrainee: boolean;
  jobTitle: string | null;
};

export enum NewProgramRole {
  Clinician = "Clinician",
  ProgramAdmin = "ProgramAdmin",
  Admin = "Admin"
}

const pageDetails: PageWrapperProps = {
  className: "NewUserInvitation",
  headerText: "New User Invitation"
};

export const NewUserInvitation = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const currentUser = useAppSelector(selectCurrentUser);
  const currentUsersPermissions = useAppSelector(selectUserCognitoGroups);

  const [existingUsersWithEmail, setExistingUsersWithEmail] = useState<
    User[] | null
  >(null);
  const [existingUsersWithNPI, setExistingUsersWithNPI] = useState<
    User[] | null
  >(null);

  const {
    programs: programsCurrentUserCanAdminister,
    institutions: institutionsCurrentUserCanAdminister
  } = getProgramsCurrentUserCanAdminister();

  const canAdministerMultiplePrograms =
    programsCurrentUserCanAdminister &&
    programsCurrentUserCanAdminister.length > 1;

  const canAdministerMultipleInstitutions =
    institutionsCurrentUserCanAdminister &&
    institutionsCurrentUserCanAdminister.length > 1;

  const isProgramAdmin =
    currentUsersPermissions &&
    currentUsersPermissions.includes(CognitoUserGroups.ProgramAdmins);

  const missingNeededProgram =
    !programsCurrentUserCanAdminister ||
    programsCurrentUserCanAdminister.length < 1;

  const missingNeededInstitution =
    !institutionsCurrentUserCanAdminister ||
    institutionsCurrentUserCanAdminister.length < 1;

  const {
    newUser,
    status: createUserStatus,
    error: createUserError,
    createUser
  } = useCreateUser();
  const alphabetizedInstitutions = institutionsCurrentUserCanAdminister
    ? alphabetizeInstitutions(institutionsCurrentUserCanAdminister)
    : institutionsCurrentUserCanAdminister;

  const {
    users: usersWithEmail,
    status: usersByEmailStatus,
    error: usersByEmailError,
    getUsersByEmail
  } = useUsersByEmail();
  const {
    users: usersWithNPI,
    status: usersByNPIStatus,
    error: usersByNPIError,
    getUsersByNPI
  } = useUsersByNPI();

  /** Form Creation */
  const defaultInstitutionId =
    institutionsCurrentUserCanAdminister && canAdministerMultipleInstitutions
      ? ""
      : institutionsCurrentUserCanAdminister?.[0]?.id;

  const defaultProgramId =
    programsCurrentUserCanAdminister && canAdministerMultiplePrograms
      ? ""
      : programsCurrentUserCanAdminister?.[0]?.id;

  const {
    register,
    handleSubmit,
    reset,
    control,
    getValues,
    watch,
    setError,
    setValue,
    formState: { errors, isDirty }
  } = useForm<NewUserInvitationFormInputs>({
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
      institutionId: defaultInstitutionId,
      programId: "",
      role: undefined,
      npi: undefined,
      simplLearnId: "",
      pgy: undefined,
      traineeType: undefined,
      isClinicianTrainee: false,
      jobTitle: ""
    }
  });

  // Need to re-render the form when:
  // - role changes so that the correct role-dependant fields are shown.
  // - institution changes so that the program list is updated properly.
  const watchers = watch(["role", "institutionId"]);

  const selectedRole = watch("role");
  const isClinician = selectedRole === NewProgramRole.Clinician;
  const isClinicianTrainee = watch("isClinicianTrainee");

  const mapNewRoleToProgramRole = (
    role: NewProgramRole,
    isClinicianTrainee: boolean
  ): ProgramRole => {
    if (role === NewProgramRole.Clinician) {
      return isClinicianTrainee ? ProgramRole.Trainee : ProgramRole.Attending;
    }
    if (role === NewProgramRole.ProgramAdmin) {
      return ProgramRole.ProgramAdmin;
    }
    if (role === NewProgramRole.Admin) {
      return ProgramRole.Admin;
    }
    // Optional: throw error if somehow an unexpected value is passed
    throw new Error(`Unsupported role: ${role}`);
  };

  const anyApiIsLoading = [usersByEmailStatus, usersByNPIStatus].includes(
    "loading"
  );
  const isSubmittingUser = createUserStatus === "loading";
  const isFormBusy = anyApiIsLoading || isSubmittingUser;

  const programsInSelectedInstitution =
    programsCurrentUserCanAdminister?.filter(
      (program) => program.institutionId === getValues("institutionId")
    );
  const alphabetizedProgramsInSelectedInstitution =
    programsInSelectedInstitution
      ? alphabetizePrograms(programsInSelectedInstitution)
      : programsInSelectedInstitution;

  const showNPI = isClinician;

  const onSubmit: SubmitHandler<NewUserInvitationFormInputs> = async (data) => {
    let deleteNewUser = false;
    const institutionIdToUse = data.institutionId ?? defaultInstitutionId;
    const programIdToUse = data.programId ?? defaultProgramId;

    if (currentUser && currentUser.id && institutionIdToUse) {
      // Check if the entered email is already in use
      const usersWithEmail = await getUsersByEmail(data.email);
      // Stop if email check failed (use hook status/error)
      if (usersByEmailStatus === "failed" || usersByEmailError) {
        setError("email", {
          type: "custom",
          message: usersByEmailError || "Email check failed"
        });
        return;
      }
      if (usersWithEmail && usersWithEmail.length > 0) {
        setError("email", {
          type: "custom",
          message: InvitationFormErrors.EmailInUseError
        });
      }
      // Check if the entered NPI is already in use
      let usersWithNPI: User[] | null = null;
      if (data.npi) {
        usersWithNPI = await getUsersByNPI(data.npi);
        // Stop if NPI check failed (use hook status/error)
        if (usersByNPIStatus === "failed" || usersByNPIError) {
          setError("npi", {
            type: "custom",
            message: usersByNPIError || "NPI check failed"
          });
          return;
        }
        if (usersWithNPI && usersWithNPI.length > 0) {
          setError("npi", {
            type: "custom",
            message: InvitationFormErrors.NpiInUseError
          });
        }
      }
      // Jump out if the npi or email is already in use.
      if (
        (usersWithEmail && usersWithEmail.length > 0) ||
        (usersWithNPI && usersWithNPI.length > 0)
      ) {
        setExistingUsersWithEmail(usersWithEmail);
        setExistingUsersWithNPI(usersWithNPI);
        return;
      }

      setExistingUsersWithEmail(null);
      setExistingUsersWithNPI(null);

      const userInput: CreateUserInput = {
        firstName: data.firstName,
        lastName: data.lastName,
        email: data.email,
        npi:
          selectedRole === NewProgramRole.Clinician && data.npi
            ? Number(data.npi)
            : null,
        simplLearnId: data.simplLearnId,
        pgy: data.pgy,
        jobTitle: data.jobTitle,
        programRole: mapNewRoleToProgramRole(
          data.role,
          data.isClinicianTrainee
        ),
        adminId: currentUser.id,
        programId: programIdToUse,
        startDate: new Date().toISOString(),
        traineeType:
          mapNewRoleToProgramRole(data.role, data.isClinicianTrainee) ===
          ProgramRole.Trainee
            ? data.traineeType
            : undefined
      };
      const createdUser = await createUser(userInput);
      // Log correctly
      console.log("createUser result:", {
        createdUser,
        createUserError,
        createUserStatus
      });

      // Navigate based on return value (not hook state)
      if (createdUser?.success && createdUser.userId) {
        navigate(`/invitations/${createdUser.userId}`);
      }
    }
  };

  useEffect(() => {
    if (selectedRole !== NewProgramRole.Clinician) {
      setValue("isClinicianTrainee", false);
      setValue("pgy", null);
      setValue("traineeType", null);
      setValue("jobTitle", null);
      setValue("npi", null);
    }
  }, [selectedRole, setValue]);

  // Automatically set the institutionId if the user only can administer within one institution
  useEffect(() => {
    reset({
      institutionId: defaultInstitutionId
    });
  }, [defaultInstitutionId]);

  // Watch for any api errors while checking for existing emails or npi
  useEffect(() => {
    if (usersByEmailError) {
      setError("email", {
        type: "custom",
        message: usersByEmailError
      });
    }
    if (usersByNPIError) {
      setError("npi", {
        type: "custom",
        message: usersByNPIError
      });
    }
  }, [usersByEmailError, usersByNPIError]);

  useEffect(() => {
    if (
      (existingUsersWithEmail || existingUsersWithNPI) &&
      setShowModal &&
      setModalContent
    ) {
      const usersAreSame =
        Array.isArray(existingUsersWithEmail) &&
        Array.isArray(existingUsersWithNPI) &&
        existingUsersWithEmail.every((ue) =>
          existingUsersWithNPI.find((un) => un.id === ue.id)
        );

      dispatch(
        setModalContent(
          <AddUserToProgram
            existingUsersWithEmail={existingUsersWithEmail}
            existingUsersWithNPI={
              usersAreSame ? undefined : existingUsersWithNPI
            }
            selectedProgramRole={mapNewRoleToProgramRole(
              getValues("role"),
              getValues("isClinicianTrainee")
            )}
            selectedProgramTraineeType={getValues("traineeType") ?? undefined}
            selectedNPI={getValues("npi") ?? undefined}
            selectedInstitutionId={getValues("institutionId")}
            selectedProgramId={getValues("programId")}
            selectedSimplLearnId={getValues("simplLearnId")}
            selectedPGY={getValues("pgy") ?? undefined}
            postSuccessAction={() => {
              dispatch(setShowModal(false));
              navigate("/users");
            }}
          />
        )
      );
      dispatch(setShowModal(true));
    }
  }, [existingUsersWithEmail, existingUsersWithNPI]);

  if (createUserStatus === "failed" || createUserError) {
    return (
      <PageWrapper {...pageDetails}>
        <h2>{t("Error")}</h2>
        <h3>{t(UserNetworkErrors.CreateUserError)}</h3>
        {createUserError && <p>{t(createUserError)}</p>}
      </PageWrapper>
    );
  }

  return (
    <PageWrapper {...pageDetails}>
      <Backdrop
        open={isSubmittingUser}
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
      <form
        className="NewUserInvitationForm"
        onSubmit={handleSubmit(onSubmit)}
        aria-busy={isSubmittingUser}
        noValidate
        role="form"
      >
        <Grid container spacing={2} pt={2} xs={12} sm={10} md={8} lg={6} xl={4}>
          {/* First Name */}
          <Grid xs={12} sm={6}>
            {/* TODO - Disable in certain scenarios (non-sysadmin trying to create a new invitation without a 'User' to be from, or an 'Institution' to add to, etc.) */}
            <Controller
              control={control}
              name="firstName"
              rules={{
                required: UserFormErrors.EmptyFirstName,
                maxLength: {
                  value: 40,
                  message: UserFormErrors.FirstNameTooLong
                }
              }}
              render={({ field: { onChange, onBlur, value } }) => (
                <TextField
                  onChange={onChange}
                  label={t("First Name")}
                  onBlur={onBlur}
                  value={value}
                  sx={{ width: "100%" }}
                  required
                  disabled={
                    isFormBusy ||
                    missingNeededProgram ||
                    missingNeededInstitution
                  }
                />
              )}
            />
            {errors.firstName?.message && (
              <Alert severity="error" sx={{ marginTop: 1 }}>
                {t(errors.firstName.message)}
              </Alert>
            )}
          </Grid>

          {/* Last Name */}
          <Grid xs={12} sm={6}>
            <Controller
              control={control}
              name="lastName"
              rules={{
                required: UserFormErrors.EmptyLastName,
                maxLength: {
                  value: 40,
                  message: UserFormErrors.LastNameTooLong
                }
              }}
              render={({ field: { onChange, onBlur, value } }) => (
                <TextField
                  onChange={onChange}
                  label={t("Last Name")}
                  onBlur={onBlur}
                  value={value}
                  sx={{ width: "100%" }}
                  required
                  disabled={
                    isFormBusy ||
                    missingNeededProgram ||
                    missingNeededInstitution
                  }
                />
              )}
            />
            {errors.lastName?.message && (
              <Alert severity="error" sx={{ marginTop: 1 }}>
                {t(errors.lastName.message)}
              </Alert>
            )}
          </Grid>

          {/* Email */}
          <Grid xs={12} sm={6}>
            <Controller
              control={control}
              name="email"
              rules={{
                required: UserFormErrors.EmptyEmail,
                pattern: {
                  value: Regex.Email,
                  message: UserFormErrors.InvalidEmail
                }
              }}
              render={({ field: { onChange, onBlur, value } }) => (
                <TextField
                  onChange={onChange}
                  label={t("Email")}
                  onBlur={onBlur}
                  value={value}
                  sx={{ width: "100%" }}
                  required
                  disabled={
                    isFormBusy ||
                    missingNeededProgram ||
                    missingNeededInstitution
                  }
                />
              )}
            />
            {errors.email?.message && (
              <Alert severity="error" sx={{ marginTop: 1 }}>
                {t(errors.email.message)}
              </Alert>
            )}
          </Grid>

          {/* An empty grid item for responsiveness */}
          <Grid
            xs={0}
            sm={6}
            sx={{
              display: { xs: "none", sm: "block" }
            }}
          />

          {/* Institution */}
          {canAdministerMultipleInstitutions && (
            <>
              <Grid xs={12} sm={6}>
                <Controller
                  control={control}
                  name="institutionId"
                  rules={{
                    required: {
                      value: true,
                      message: InvitationFormErrors.InstitutionRequiredError
                    }
                  }}
                  render={({ field: { onChange, onBlur, value } }) => (
                    <FormControl fullWidth>
                      <InputLabel
                        id="invitation-institution-label"
                        required
                        disabled={
                          isFormBusy ||
                          missingNeededProgram ||
                          missingNeededInstitution
                        }
                      >
                        {t("Institution")}
                      </InputLabel>
                      <Select
                        labelId="invitation-institution-label"
                        data-testid={"invitation-institution-input-group"}
                        id="institutionId"
                        value={value || ""}
                        label={t("Institution")}
                        onChange={onChange}
                        onBlur={onBlur}
                        required
                        disabled={
                          isFormBusy ||
                          missingNeededProgram ||
                          missingNeededInstitution
                        }
                      >
                        <MenuItem value={""} key={"no-institution"}>
                          {t("(none)")}
                        </MenuItem>
                        {alphabetizedInstitutions?.map((institution, index) => (
                          <MenuItem
                            value={institution.id}
                            key={`${index}-${institution.id}`}
                          >
                            {institution.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                />
                {errors.institutionId?.message && (
                  <Alert
                    severity={
                      errors.institutionId?.message ? "error" : "warning"
                    }
                    sx={{ marginTop: 1 }}
                  >
                    {t(
                      errors.institutionId?.message ||
                        InstitutionNotifications.NoInstitutions
                    )}
                  </Alert>
                )}
              </Grid>

              {/* An empty grid item for responsiveness */}
              <Grid
                xs={0}
                sm={6}
                sx={{
                  display: { xs: "none", sm: "block" }
                }}
              />
            </>
          )}

          {/* Program */}
          {canAdministerMultiplePrograms && (
            <>
              <Grid xs={12} sm={6}>
                <Controller
                  control={control}
                  name={"programId"}
                  rules={{
                    required: {
                      value: true,
                      message: InvitationFormErrors.ProgramRequiredError
                    }
                  }}
                  render={({ field: { onChange, onBlur, value } }) => (
                    <FormControl fullWidth>
                      <InputLabel
                        id="invitation-program-label"
                        required
                        disabled={
                          !getValues("institutionId") ||
                          isFormBusy ||
                          missingNeededProgram ||
                          missingNeededInstitution
                        }
                      >
                        {t("Program")}
                      </InputLabel>
                      <Select
                        labelId="invitation-program-label"
                        data-testid={"invitation-program-input-group"}
                        id="programId"
                        value={value || ""}
                        label={t("Program")}
                        onChange={onChange}
                        onBlur={onBlur}
                        required
                        disabled={
                          !getValues("institutionId") ||
                          isFormBusy ||
                          missingNeededProgram ||
                          missingNeededInstitution
                        }
                      >
                        <MenuItem value={""} key={"no-program"}>
                          {t("(none)")}
                        </MenuItem>
                        {alphabetizedProgramsInSelectedInstitution &&
                          alphabetizedProgramsInSelectedInstitution.map(
                            (program, index) => (
                              <MenuItem
                                value={program.id}
                                key={`${index}-${program.id}`}
                              >
                                {`${program.name} - (${program.institution?.name})`}
                              </MenuItem>
                            )
                          )}
                      </Select>
                    </FormControl>
                  )}
                />
                {(errors.programId?.message ||
                  (getValues("institutionId") &&
                    (!alphabetizedProgramsInSelectedInstitution ||
                      alphabetizedProgramsInSelectedInstitution.length < 1))) && (
                  <Alert
                    severity={errors.programId?.message ? "error" : "warning"}
                    sx={{ marginTop: 1 }}
                  >
                    {t(
                      errors.programId?.message ||
                        ProgramNotifications.NoProgramsInInstitution
                    )}
                  </Alert>
                )}
              </Grid>

              {/* An empty grid item for responsiveness */}
              <Grid
                xs={0}
                sm={6}
                sx={{
                  display: { xs: "none", sm: "block" }
                }}
              />
            </>
          )}

          {/* Role */}
          <>
            <Grid xs={12} sm={6}>
              <Controller
                control={control}
                name="role"
                rules={{ required: InvitationFormErrors.RoleRequiredError }}
                render={({ field: { onChange, onBlur, value } }) => (
                  <FormControl fullWidth>
                    <InputLabel
                      id="invitation-role-label"
                      required
                      disabled={
                        isFormBusy ||
                        missingNeededProgram ||
                        missingNeededInstitution
                      }
                    >
                      {t("Role")}
                    </InputLabel>
                    <Select
                      labelId="invitation-role-label"
                      data-testid={"invitation-role-input-group"}
                      id="role"
                      value={value || ""}
                      label={t("Role")}
                      onChange={onChange}
                      onBlur={onBlur}
                      required
                      disabled={
                        isFormBusy ||
                        missingNeededProgram ||
                        missingNeededInstitution
                      }
                    >
                      <MenuItem value="">{t("(Select a role)")}</MenuItem>
                      {ProgramRoleSelectFields.map((programRole, index) => {
                        if (
                          isProgramAdmin &&
                          programRole.value === ProgramRole.Admin
                        )
                          return null;
                        return (
                          <MenuItem
                            value={programRole.value}
                            key={`${index}-${programRole.value}`}
                          >
                            {programRole.label}
                          </MenuItem>
                        );
                      })}
                    </Select>
                  </FormControl>
                )}
              />
              {errors.role?.message && (
                <Alert severity="error" sx={{ marginTop: 1 }}>
                  {t(errors.role.message)}
                </Alert>
              )}
            </Grid>
            {/* An empty grid item for responsiveness */}
            <Grid
              xs={0}
              sm={6}
              sx={{
                display: { xs: "none", sm: "block" }
              }}
            />
          </>

          {/* Is Trainee or not Radio */}
          {isClinician && (
            <>
              <Grid xs={12} sm={6}>
                <Controller
                  name="isClinicianTrainee"
                  control={control}
                  rules={{
                    validate: (value) =>
                      value !== undefined || "This field is required."
                  }}
                  render={({
                    field: { onChange, value },
                    fieldState: { error }
                  }) => (
                    <FormControl
                      component="fieldset"
                      error={!!error}
                      required
                      sx={{ width: "100%" }}
                    >
                      <FormLabel component="legend" sx={{ mb: 1 }}>
                        {t(
                          "Is this user a trainee in your program about whom you would like to collect assessments?"
                        )}
                      </FormLabel>
                      <RadioGroup
                        sx={{ ml: 2 }}
                        row
                        value={value === undefined ? "" : value ? "yes" : "no"}
                        onChange={(e) => {
                          const selected = e.target.value === "yes";
                          onChange(selected);
                        }}
                      >
                        <FormControlLabel
                          value="yes"
                          control={<Radio />}
                          label={t("Yes")}
                        />
                        <FormControlLabel
                          value="no"
                          control={<Radio />}
                          label={t("No")}
                        />
                      </RadioGroup>
                      {error && (
                        <FormHelperText>{error.message}</FormHelperText>
                      )}
                    </FormControl>
                  )}
                />
              </Grid>
              {/* An empty grid item for responsiveness */}
              <Grid
                xs={0}
                sm={6}
                sx={{
                  display: { xs: "none", sm: "block" }
                }}
              />
            </>
          )}

          {/* Job Title below */}
          {isClinician && (
            <>
              <Grid xs={12} sm={6}>
                <Controller
                  control={control}
                  name="jobTitle"
                  rules={{
                    required: {
                      value: true,
                      message: "Job Title is required"
                    }
                  }}
                  render={({ field: { onChange, onBlur, value } }) => (
                    <TextField
                      onChange={onChange}
                      label={t("Job Title")}
                      onBlur={onBlur}
                      value={value || ""}
                      sx={{ width: "100%" }}
                      required
                      disabled={
                        isFormBusy ||
                        missingNeededProgram ||
                        missingNeededInstitution
                      }
                    />
                  )}
                />
                {errors.jobTitle?.message && (
                  <Alert severity="error" sx={{ marginTop: 1 }}>
                    {t(errors.jobTitle.message)}
                  </Alert>
                )}
              </Grid>
              {/* An empty grid item for responsiveness */}
              <Grid
                xs={0}
                sm={6}
                sx={{
                  display: { xs: "none", sm: "block" }
                }}
              />
            </>
          )}

          {/* NPI */}
          {showNPI && (
            <>
              <Grid xs={12} sm={6}>
                <Controller
                  control={control}
                  name="npi"
                  rules={{
                    validate: (value) =>
                      (value && !isNaN(value)) ||
                      InvitationFormErrors.NPIOutOfRangeError,
                    min: {
                      value: 1000000000,
                      message: InvitationFormErrors.NPIOutOfRangeError
                    },
                    max: {
                      value: 2999999999,
                      message: InvitationFormErrors.NPIOutOfRangeError
                    },
                    required: {
                      value: showNPI,
                      message: InvitationFormErrors.NPIRequiredError
                    }
                  }}
                  render={({ field: { onChange, onBlur, value } }) => (
                    <TextField
                      onChange={onChange}
                      onBlur={onBlur}
                      label={t("NPI")}
                      value={value || ""}
                      sx={{ width: "100%" }}
                      required
                      inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }}
                      disabled={
                        isFormBusy ||
                        missingNeededProgram ||
                        missingNeededInstitution
                      }
                    />
                  )}
                />
                {errors.npi?.message && (
                  <Alert severity="error" sx={{ marginTop: 1 }}>
                    {t(errors.npi.message)}
                  </Alert>
                )}
              </Grid>
              {/* An empty grid item for responsiveness */}
              <Grid
                xs={0}
                sm={6}
                sx={{
                  display: { xs: "none", sm: "block" }
                }}
              />
            </>
          )}

          {/* SIMPLLearnId */}
          {showNPI && (
            <>
              <Grid xs={12} sm={6}>
                <Controller
                  control={control}
                  name="simplLearnId"
                  render={({ field: { onChange, onBlur, value } }) => (
                    <TextField
                      onChange={onChange}
                      onBlur={onBlur}
                      label={t("SIMPL LearnID")}
                      value={value || ""}
                      sx={{ width: "100%" }}
                      disabled={
                        isFormBusy ||
                        missingNeededProgram ||
                        missingNeededInstitution
                      }
                    />
                  )}
                />
                {errors.simplLearnId?.message && (
                  <Alert severity="error" sx={{ marginTop: 1 }}>
                    {t(errors.simplLearnId.message)}
                  </Alert>
                )}
              </Grid>
              {/* An empty grid item for responsiveness */}
              <Grid
                xs={0}
                sm={6}
                sx={{
                  display: { xs: "none", sm: "block" }
                }}
              />
            </>
          )}

          {/* PGY */}
          {isClinicianTrainee && (
            <>
              <Grid xs={12} sm={6}>
                <Controller
                  control={control}
                  name="pgy"
                  rules={{
                    required: {
                      value: isClinicianTrainee,
                      message: InvitationFormErrors.PGYRequiredError
                    }
                  }}
                  render={({ field: { onChange, onBlur, value } }) => (
                    <FormControl fullWidth>
                      <InputLabel
                        id="invitation-pgy-label"
                        required
                        disabled={
                          isFormBusy ||
                          missingNeededProgram ||
                          missingNeededInstitution
                        }
                      >
                        {t("PGY")}
                      </InputLabel>
                      <Select
                        labelId="invitation-pgy-label"
                        data-testid={"invitation-pgy-input-group"}
                        id="role"
                        value={value || ""}
                        label={t("PGY")}
                        onChange={onChange}
                        onBlur={onBlur}
                        required
                        disabled={
                          isFormBusy ||
                          missingNeededProgram ||
                          missingNeededInstitution
                        }
                      >
                        {PGYSelectFields.map((pgy, index) => (
                          <MenuItem
                            value={pgy.value}
                            key={`${index}-${pgy.label}`}
                          >
                            {pgy.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                />
                {errors.pgy?.message && (
                  <Alert severity="error" sx={{ marginTop: 1 }}>
                    {t(errors.pgy.message)}
                  </Alert>
                )}
              </Grid>
              {/* An empty grid item for responsiveness */}
              <Grid
                xs={0}
                sm={6}
                sx={{
                  display: { xs: "none", sm: "block" }
                }}
              />
            </>
          )}

          {/* TraineeType */}
          {isClinicianTrainee && (
            <>
              <Grid xs={12} sm={6}>
                <Controller
                  control={control}
                  name="traineeType"
                  rules={{
                    required: {
                      value: isClinicianTrainee,
                      message: InvitationFormErrors.TraineeTypeRequiredError
                    }
                  }}
                  render={({ field: { onChange, onBlur, value } }) => (
                    <FormControl fullWidth>
                      <InputLabel
                        id="invitation-trainee-type-label"
                        required
                        disabled={
                          isFormBusy ||
                          missingNeededProgram ||
                          missingNeededInstitution
                        }
                      >
                        {t("Trainee Type")}
                      </InputLabel>
                      <Select
                        labelId="invitation-trainee-type-label"
                        data-testid={"invitation-trainee-type-input-group"}
                        id="role"
                        value={value || ""}
                        label={t("Trainee Type")}
                        onChange={onChange}
                        onBlur={onBlur}
                        required
                        disabled={
                          isFormBusy ||
                          missingNeededProgram ||
                          missingNeededInstitution
                        }
                      >
                        {TraineeTypeSelectFields.map((traineeType, index) => (
                          <MenuItem
                            value={traineeType.value}
                            key={`${index}-${traineeType.label}`}
                          >
                            {traineeType.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                />
                {errors.traineeType?.message && (
                  <Alert severity="error" sx={{ marginTop: 1 }}>
                    {t(errors.traineeType.message)}
                  </Alert>
                )}
              </Grid>
              {/* An empty grid item for responsiveness */}
              <Grid
                xs={0}
                sm={6}
                sx={{
                  display: { xs: "none", sm: "block" }
                }}
              />
            </>
          )}

          <Grid xs={12} container spacing={2}>
            <Grid>
              <Button
                disabled={
                  isFormBusy ||
                  missingNeededProgram ||
                  missingNeededInstitution
                }
                variant="contained"
                type="submit"
              >
                {t("Submit")}
              </Button>
            </Grid>
            <Grid>
              <Button
                disabled={isFormBusy}
                onClick={() => navigate("/users")}
                variant="outlined"
              >
                {t("Cancel")}
              </Button>
            </Grid>
          </Grid>
          {missingNeededProgram && (
            <Grid xs={12}>
              <Alert severity="warning" sx={{ marginTop: 1 }}>
                {t(InvitationFormErrors.NoProgramForCurrentUser)}
              </Alert>
            </Grid>
          )}
          {missingNeededInstitution && (
            <Grid xs={12}>
              <Alert severity="warning" sx={{ marginTop: 1 }}>
                {t(InvitationFormErrors.NoInstitutionForCurrentUser)}
              </Alert>
            </Grid>
          )}
        </Grid>
      </form>
    </PageWrapper>
  );
};
